import Container, { Service } from 'typedi';
import { TFunction } from 'i18next';
import { AvailableStatusEnum, GetAllLabourParams, LabourDocument, mapSingleOccupationCaseInsensitive, mapSkillsCaseInsensitive, mapTicketsCaseInsensitive, OccupationType } from '../interfaces/labour.interface';
import User from '../../../Modules/Auth/models/auth.model';
import { HttpException } from '../../../utils/exceptions/httpException';
import { status } from '../../../utils/helpers/api.responses';
import Role from '../../../Modules/Role/models/role.model';
import Labour from '../models/labour.model';
import { AttachmentService } from '../../../Modules/Attachment/services/attachment.service';
import { IAttachment } from '../../../Modules/Attachment/interfaces/attcahment.interface';
import { aws } from '../../../config/index';
import mongoose, { Types } from 'mongoose';
import { IUser, RolesEnum, StatusEnum } from '../../../Modules/Auth/interfaces/auth.interface';
import { NotificationService } from '../../../Modules/Notification/services/notification.service';
import statusTicket from '../constant/status.json'
import Job from 'Modules/jobManagement/models/job.model';
import AllocationRequest from 'Modules/allocationRequest/models/allocationRequest.model';
import { AllocationRequestStatusEnum } from 'Modules/allocationRequest/interfaces/allocationRequest.interface';
import Availability from 'Modules/availabilityManagement/models/availability.model';
import { startOfDay, endOfDay, addHours } from 'date-fns';
import Message from 'Modules/Chat/models/message.model';




@Service()
export class LabourService {

    public attachmentService = Container.get(AttachmentService)
    public notification = Container.get(NotificationService)



    public async labourDashboard(userId: string, t: TFunction): Promise<any> {
        try {
            const [user, labour] = await Promise.all([
                User.findOne({ _id: userId, isDeleted: false })
                    .populate<{ profileImage: { _id: Types.ObjectId; filename: string; key: string; thumbnailKey?: string, fileSize: number } }>('profileImage'),
                Labour.findOne({ userId, isDeleted: false })
            ])
            if (!user || !labour) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }))
            }
            const allocationRequests = await AllocationRequest.find({ userId, isDeleted: false })
                .sort({ respondedAt: -1, createdAt: -1 })
                .populate('jobId', 'companyName location noOfSpots open')
                .lean();
            const pendingRequests = allocationRequests.filter(req => req.status.toLowerCase() === AllocationRequestStatusEnum.PENDING.toLowerCase());
            const acceptedRequests = allocationRequests.filter(req => req.status.toLowerCase() === AllocationRequestStatusEnum.ACCEPTED.toLowerCase());
            const rejectedRequests = allocationRequests.filter(req => req.status.toLowerCase() === AllocationRequestStatusEnum.REJECTED.toLowerCase());
            const recentRequests = allocationRequests.slice(0, 5);
            // const availableJobs = await Job.find({
            //     status: 'Open',
            //     open: { $gt: 0 },
            //     isDeleted: false,
            // })
            //     .sort({ createdAt: -1 })
            //     .limit(5)
            //     .select('companyName location noOfSpots open note')
            //     .lean();

            const totalAccepted = acceptedRequests.length;
            const upcomingWork = acceptedRequests.filter(req => new Date(req.endDate) > new Date()).length;
            const unreadChatCount = await Message.countDocuments({ receiverId: userId, isRead: false, isDeleted: false });
            const dashboardData = {
                user: {
                id: user._id,
                email: user.email,
                status: user.status,
                fullName: user.fullName || `${user.firstName} ${user.lastName}`,
                profileImage: user.profileImage ?
                    {
                        _id: user.profileImage._id,
                        filename: user.profileImage.filename,
                        url: `${aws.S3_BASE_URL}/${user.profileImage.key}`,
                        thumbnailUrl: user.profileImage.thumbnailKey ?
                            `${aws.S3_BASE_URL}/${user.profileImage.thumbnailKey}` : null,
                        fileSize: user.profileImage.fileSize

                    } : null,

                },
                labour: {
                employeeId: labour.employee_id,
                availabilityStatus: labour.availability_status,
                profileCompletionStatus: labour.profileCompletionStatus,
                profileCompletionPercentage: labour.profileCompletionPercentage || 0,
                averageRating: labour.averageRating,
                ratingCount: labour.ratingCount,
                occupation: labour.occupation,
                tickets: Array.isArray(labour.tickets)
                    ? labour.tickets.map(ticketCode => statusTicket.tickets[ticketCode] ?? ticketCode)
                    : [],
                sitesInductedOn: labour.sitesInductedOn ?? [],

                },
                allocationRequests: {
                pendingRequest: pendingRequests.length, // if need is to display details then remove length
                acceptedRequest: acceptedRequests.length, // if need is to display details then remove length
                rejectedRequest: rejectedRequests.length, // if need is to display details then remove length
                recentRequest: recentRequests.length, // if need is to display details then remove length
                },
                // availableJobs,
                summary: {
                totalAccepted,
                unreadChatCount,
                upcomingWork,
                lastUpdated: new Date(),
                },
            };
            return dashboardData
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorFetching', t('Labour.labourDashboard')))
        }
    }
    public async adminDashboard(userId: string, t: TFunction): Promise<any> {
        try {
            const user = await User.findOne({ _id: userId, isDeleted: false })
                .populate('roleId', 'name')
                .populate<{ profileImage: { _id: Types.ObjectId; filename: string; key: string; thumbnailKey?: string, fileSize: number } }>('profileImage')
                .lean();
            if (!user || (user.roleId as any)?.name !== RolesEnum.ADMIN) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'));
            }
            const labourRole = await Role.findOne({
                name: { $regex: `^${RolesEnum.LABOUR}$`, $options: 'i' },
            });
            let now = new Date();
            now = addHours(now, 5 + 30 / 60); // Adjust to IST (01:28 PM IST on July 11, 2025)

            const [totalJobs, openJobs, closedJobs, totalLaborers, availableLaborers, unavailableLaborers,
                totalAllocationRequests, pendingRequests, acceptedRequests, rejectedRequests, totalStaff, unreadChatCount] = await Promise.all([
                    Job.countDocuments({ isDeleted: false }),
                    Job.countDocuments({ status: 'Open', isDeleted: false }),
                    Job.countDocuments({ status: 'Closed', isDeleted: false }),
                    Labour.countDocuments({ isDeleted: false }),
                    Labour.countDocuments({
                        isDeleted: false,
                        _id: {
                            $nin: await this.getUnavailableLabourIds(now),
                        },
                    }),
                    Labour.countDocuments({
                        isDeleted: false,
                        _id: {
                            $in: await this.getUnavailableLabourIds(now),
                        },
                    }),
                    AllocationRequest.countDocuments({ isDeleted: false }),
                    AllocationRequest.countDocuments({ status: 'Pending', isDeleted: false }),
                    AllocationRequest.countDocuments({ status: 'Accepted', isDeleted: false }),
                    AllocationRequest.countDocuments({ status: 'Rejected', isDeleted: false }),
                    User.countDocuments({ isDeleted: false, roleId: { $ne: user.roleId } }),
                    Message.countDocuments({ receiverId: userId, isRead: false, isDeleted: false })
                ]);
            const activeLaborers = await Labour.countDocuments({ isDeleted: false, status: StatusEnum.ACTIVE });
            const awaitingLaborers = await Labour.countDocuments({ isDeleted: false, status: StatusEnum.WAITING_TO_ACCEPT });
            const deactivatedLaborers = await Labour.countDocuments({ isDeleted: false, status: StatusEnum.DEACTIVATED });

            // Fetch recent data with timestamps
            const recentJobs = await Job.find({ isDeleted: false })
                .sort({ createdAt: -1 })
                .limit(5)
                .select('companyName location noOfSpots status open allotted createdAt srno')
                .lean();

            const recentLaborers = await Labour.find({ isDeleted: false })
                .sort({ createdAt: -1 })
                .limit(5)
                .populate('userId', 'firstName lastName email')
                .select('employee_id availability_status status created_at')
                .lean();

            const recentAllocationRequests = await AllocationRequest.find({ isDeleted: false })
                .sort({ respondedAt: -1, createdAt: -1 })
                .limit(5)
                .populate('userId', 'firstName lastName email')
                .populate('jobId', 'companyName location noOfSpots status')
                .select('userId requestDate startDate endDate status responseNotes respondedAt jobId wage allocationNotes')
                .lean();

            const pendingAllocationRequests = await AllocationRequest.find({
                isDeleted: false,
                status: AllocationRequestStatusEnum.PENDING,
                requestDate: { $gte: new Date(now.getTime() - 24 * 60 * 60 * 1000) } // Last 24 hours
            })
                .sort({ requestDate: -1 })
                .limit(5)
                .populate('userId', 'firstName lastName email')
                .populate('jobId', 'companyName location noOfSpots status')
                .select('userId requestDate startDate endDate status responseNotes respondedAt jobId wage allocationNotes')
                .lean();

            const recentlyOnboardedLabours = await User.find({
                isDeleted: false,
                onboardingTime: { $gte: new Date(now.getTime() - 24 * 60 * 60 * 1000) },
                roleId: labourRole._id
            })
                .sort({ onboardingTime: -1 })
                .limit(5)
                .populate('profileImage')
                .lean();

            // Combine all recent updates into a single array
            const recentUpdates = [
                ...recentJobs.map(job => ({
                    type: 'job',
                    timestamp: job.createdDate,
                    status: job.status,
                    data: job,
                    // message: `New job "${job.companyName}" created`
                })),
                ...recentLaborers.map(labour => ({
                    type: 'labour',
                    timestamp: labour.createdAt,
                    status: (labour.userId as any).status,
                    data: labour,
                    // message: `${(labour.userId as any).firstName} ${(labour.userId as any).lastName} joined as labour`
                })),
                ...recentAllocationRequests.map(request => ({
                    type: 'allocationRequest',
                    timestamp: request.respondedAt || request.requestDate,
                    status: request.status,
                    data: request,
                    // message: `${(request.userId as any).firstName} ${(request.userId as any).lastName} ${request.status} allocation request for "${(request.jobId as any).companyName}"`
                })),
                ...pendingAllocationRequests.map(request => ({
                    type: 'pendingAllocationRequest',
                    timestamp: request.requestDate,
                    status: request.status,
                    data: request,
                    // message: `${(request.userId as any).firstName} ${(request.userId as any).lastName} submitted a pending allocation request for "${(request.jobId as any).companyName}"`
                })),
                ...recentlyOnboardedLabours.map(labour => ({
                    type: 'onboardedLabour',
                    timestamp: labour.onboardingTime,
                    // status: 'onboarded',
                    data: labour,
                    // message: `${labour.firstName} ${labour.lastName} onboarded`
                }))
            ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .slice(0, 15);

            const dashboardData = {
                user: {
                    id: user._id,
                    email: user.email,
                    status: user.status,
                    fullName: user.fullName || `${user.firstName} ${user.lastName}`,
                    profileImage: user.profileImage ?
                        {
                            _id: user.profileImage._id,
                            filename: user.profileImage.filename,
                            url: `${aws.S3_BASE_URL}/${user.profileImage.key}`,
                            thumbnailUrl: user.profileImage.thumbnailKey ?
                                `${aws.S3_BASE_URL}/${user.profileImage.thumbnailKey}` : null,
                            fileSize: user.profileImage.fileSize
                        } : null
                },
                summary: {
                    totalJobs,
                    openJobs,
                    closedJobs,
                    totalLaborers,
                    activeLaborers,
                    awaitingLaborers,
                    deactivatedLaborers,
                    availableLaborers,
                    unavailableLaborers,
                    totalAllocationRequests,
                    pendingRequests,
                    acceptedRequests,
                    rejectedRequests,
                    totalStaff,
                },
                recentUpdates, // Replaced individual recent lists with consolidated updates
                lastUpdated: now,
                unreadChatCount
            };

            return dashboardData;
        } catch (error) {
            console.log(error);
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Labour.adminDashboard') }));
        }
    }
    public async getUnavailableLabourIds(now: Date): Promise<string[]> {
        const startOfToday = startOfDay(now);
        const endOfToday = endOfDay(now);

        const unavailableFromAvailability = await Availability.find({
            status: { $regex: `^${AvailableStatusEnum.UNAVAILABLE}$`, $options: 'i' },
            isDeleted: false,
            $or: [
                { startDate: { $lte: endOfToday }, endDate: { $gte: startOfToday } },
            ],
        }).distinct('userId');

        const unavailableFromAllocations = await AllocationRequest.find({
            status: { $regex: `^${AllocationRequestStatusEnum.ACCEPTED}$`, $options: 'i' },
            isDeleted: false,
            startDate: { $lte: endOfToday },
            endDate: { $gte: startOfToday },
        }).distinct('userId');

        const allUnavailableIds = [...new Set([
            ...unavailableFromAvailability.map((id: any) => id.toString()),
            ...unavailableFromAllocations.map((id: any) => id.toString()),
        ])];
        return allUnavailableIds;
    }

    public async updateLabourProfile(userId: string, currentUser: string, data: Partial<LabourDocument & IUser>, t: TFunction) {
        const session = await mongoose.startSession();
        let transactionCommitted = false;
        try {
            session.startTransaction();
            const { step } = data
            if (!step || ![1, 2, 3].includes(Number(step))) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'step' }));
            }
            if (userId.toString() !== currentUser.toString()) {
                throw new HttpException(status.Forbidden, t('Role.permission'))
            }
            const user = await User.findOne({ _id: userId, isDeleted: false })
                .session(session)
                .lean();
            if (!user) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            const labour = await Labour.findOne({ userId })
                .session(session);
            if (!labour) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Labour.labour') }));
            }
            const normalizedProfileImage =
                data.profileImage === undefined || data.profileImage === null || String(data.profileImage) === ''
                    ? null
                    : data.profileImage;


            const currentUserData = await User.findById(currentUser)
                .select('_id roleId firstName lastName')
                .session(session)
                .lean();
            if (!currentUserData) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.currentUser') }));
            }
            const adminRole = await Role.findOne({ name: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') })
                .session(session)
                .lean();
            const staffRole = await Role.findOne({ name: new RegExp(`^${RolesEnum.STAFF}$`, 'i') })
                .session(session)
                .lean();
            const labourRole = await Role.findOne({ name: new RegExp(`^${RolesEnum.LABOUR}$`, 'i') })
                .session(session)
                .lean();


            if (!user) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }
            const isDraft = typeof data.draft === 'boolean' ? data.draft : false;
            if (!labour) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Labour.labour') }));
            }
            if (!currentUserData) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.currentUser') }));
            }


            if (!labour.isFirstUpdate && !isDraft) {
                if (step === 1) {
                    // if (normalizedProfileImage && !Types.ObjectId.isValid(normalizedProfileImage)) {
                    //     throw new HttpException(
                    //         status.BadRequest,
                    //         t('General.invalid', { field: t('User.profileImage') })
                    //     );
                    // }

                    const step1Fields = ['firstName', 'lastName', 'phone', 'countryCode'];
                    for (const field of step1Fields) {
                        if (!data[field]) {
                            throw new HttpException(
                                status.BadRequest,
                                t('General.required', { field: t(`${field}`) })
                            );
                        }
                    }



                } else if (step === 2) {

                    const step2Fields = ['occupation', 'tickets', 'sitesInductedOn'];
                    for (const field of step2Fields) {
                        const value = data[field];

                        if (
                            value === undefined || value === null ||
                            (typeof value === 'string' && value.trim() === '') ||
                            (Array.isArray(value) && value.length === 0)
                        ) {
                            throw new HttpException(
                                status.BadRequest,
                                t('General.required', { field: t(`${field}`) })
                            );
                        }
                    }

                } else if (step === 3) {
                    if (!data.identification || data.identification.length === 0) {
                        throw new HttpException(
                            status.BadRequest,
                            t('General.required', { field: t('identification') })
                        );
                    }
                    const idTypes = data.identification.map(id => id.type) || [];
                    for (const type of Object.values(statusTicket.requiredIdTypes)) {
                        if (!idTypes.includes(type)) {
                            throw new HttpException(
                                status.BadRequest,
                                t('General.required', { field: t(`${type}`) })
                            );
                        }
                    }
                    for (const id of data.identification) {
                        const rule = statusTicket.expirationRules[id.type];
                        if (rule && id.issuedAt) {
                            const expirationDate = new Date();
                            expirationDate.setTime(expirationDate.getTime() - rule.duration * (rule.unit === 'days' ? 86400000 : 2592000000));
                            if (new Date(id.issuedAt) < expirationDate) {
                                throw new HttpException(
                                    status.BadRequest,
                                    t('Labour.expired', { field: t(`${id.type.toLowerCase().replace(/\s+/g, '')}`) })
                                );
                            }
                        }
                    }
                }
            }



            if (data.skills) {
                data.skills = mapSkillsCaseInsensitive(data.skills);
            }

            if (data.tickets) {
                data.tickets = mapTicketsCaseInsensitive(data.tickets);
            }
            if (data.occupation !== undefined) {
                if (typeof data.occupation !== 'string' || data.occupation.trim() === '') {
                    throw new HttpException(status.BadRequest, t('General.required', { field: 'occupation' }));
                }
                data.occupation = mapSingleOccupationCaseInsensitive(data.occupation);
            }

            if (Array.isArray(data.deletedFiles) && data.deletedFiles.length > 0) {
                const validDeletedIds = data.deletedFiles.filter(id => Types.ObjectId.isValid(id));

                if (validDeletedIds.length > 0) {
                    await this.attachmentService.deleteDocuments(validDeletedIds, t);
                }
            }

            const attachmentIds = [
                // ...(data.profileImage && Types.ObjectId.isValid(data.profileImage) ? [data.profileImage] : []),
                ...(normalizedProfileImage && Types.ObjectId.isValid(normalizedProfileImage) ? [normalizedProfileImage] : []),

                ...(data.certificates?.map(cert => cert.file).filter(id => id && Types.ObjectId.isValid(id)) || []),
                ...(data.identification?.map(id => id.file).filter(id => id && Types.ObjectId.isValid(id)) || []),

            ];


            if (attachmentIds.length > 0) {
                const attachments = await this.attachmentService.getAttachmentsByIds(attachmentIds, t, session);

                if (attachments.length !== attachmentIds.length) {
                    throw new HttpException(status.BadRequest, t('General.invalid', { field: t('Upload.file') }));
                }

                for (const attachment of attachments) {
                    let expectedType: string | null = null;

                    if (data.profileImage && data.profileImage.toString() === attachment._id.toString()) {
                        expectedType = 'profileImage';
                    } else if (data.certificates?.some(cert => cert?.file?.toString() === attachment._id.toString())) {
                        expectedType = 'certificate';
                    } else if (data.identification?.some(id => id?.file?.toString() === attachment._id.toString())) {
                        expectedType = 'identification';
                    }
                    if (attachment.type !== expectedType) {
                        throw new HttpException(status.BadRequest, t('Upload.invalid_file_type', { id: attachment._id }));
                    }

                }

                await this.attachmentService.updateMatliIsTemporaryFalse(attachmentIds.join(','), t, session);
            }

            const userUpdates: Partial<IUser> = {
                ...(data.firstName && { firstName: data.firstName }),
                ...(data.lastName && { lastName: data.lastName }),
                ...(data.phone && { phone: data.phone }),
                // ...(data.profileImage && { profileImage: data.profileImage }),
                ...(normalizedProfileImage !== undefined && { profileImage: normalizedProfileImage }),
                ...(data.notificationPreferences && { notificationPreferences: data.notificationPreferences }),
                ...(data.gender && { gender: data.gender }),
                ...(data.date_of_birth && { date_of_birth: data.date_of_birth }),
                ...(data.countryCode && { countryCode: data.countryCode })
            };
            if (Object.keys(userUpdates).length > 0) {
                await User.updateOne(
                    { _id: userId },
                    { $set: userUpdates },
                    { session }
                );
            }

            const updatedFields: string[] = Object.keys(userUpdates);
            const labourUpdates: Partial<LabourDocument> = {
                ...(data.occupation && { occupation: data.occupation, updatedFields: [...updatedFields, 'occupation'] }),
                ...(data.experienceSummary && { experienceSummary: data.experienceSummary, updatedFields: [...updatedFields, 'experienceSummary'] }),
                ...(data.tickets && { tickets: data.tickets, updatedFields: [...updatedFields, 'tickets'] }),
                ...(data.certificates && { certificates: data.certificates, updatedFields: [...updatedFields, 'certificates'] }),
                ...(data.identification && { identification: data.identification, updatedFields: [...updatedFields, 'identification'] }),
                ...(data.skills && { skills: data.skills, updatedFields: [...updatedFields, 'skills'] }),
                ...(data.sitesInductedOn && { sitesInductedOn: data.sitesInductedOn, updatedFields: [...updatedFields, 'sitesInductedOn'] }),
                draft: isDraft,
                isFirstUpdate: labour.isFirstUpdate && !isDraft ? false : labour.isFirstUpdate,
            };

            await Promise.all([
                Object.keys(userUpdates).length > 0 ? User.updateOne({ _id: userId }, { $set: userUpdates }, { session }) : Promise.resolve(),
                Labour.updateOne({ userId }, { $set: labourUpdates }, { session }),
            ]);


            const updatedUser = await User.findOne({ _id: userId })
                .populate<{ profileImage: { _id: Types.ObjectId; filename: string; key: string, fileSize: number } }>('profileImage')
                .session(session)
                .lean();

            const updatedLabour = await Labour.findOne({ userId })
                .populate<{ certificates: Array<{ name: string; file: { _id: Types.ObjectId; filename: string; key: string; thumbnailKey: string, fileSize: number } }> }>('certificates.file')
                .populate<{ identification: Array<{ type: string; file: { _id: Types.ObjectId; filename: string; key: string; thumbnailKey: string, fileSize: number }; issuedAt: Date; expiresAt: Date }> }>('identification.file')
                .session(session)
                .lean();

            const notificationQueue: Array<{ module: string, action: string, data: any }> = [];
            const isSelf = userId === currentUser;
            const adminRoleId = adminRole?._id;
            const staffRoleId = staffRole?._id;
            const labourRoleId = labourRole?._id;
            let notificationAction = '';
            let recipients: Types.ObjectId[] = [];


            if (!isDraft) {
                const hasPhotoID = Array.isArray(updatedLabour.identification) && updatedLabour.identification.some(id => id.type.toLowerCase() === statusTicket.requiredIdTypes.Photo_ID.toLowerCase());
                const hasPoliceClearance = Array.isArray(updatedLabour.identification) && updatedLabour.identification.some(id => id.type.toLowerCase() === statusTicket.requiredIdTypes.Police_Clearance.toLowerCase());

                const completionChecks = [
                    { field: 'firstName', value: !!updatedUser.firstName, actual: updatedUser.firstName },
                    { field: 'lastName', value: !!updatedUser.lastName, actual: updatedUser.lastName },
                    { field: 'email', value: !!updatedUser.email, actual: updatedUser.email },
                    { field: 'phone', value: !!updatedUser.phone, actual: updatedUser.phone },
                    { field: 'gender', value: !!updatedUser.gender, actual: updatedUser.gender },
                    { field: 'countryCode', value: !!updatedUser.countryCode, actual: updatedUser.countryCode },
                    { field: 'date_of_birth', value: !!updatedUser.date_of_birth, actual: updatedUser.date_of_birth },
                    { field: 'profileImage', value: !!updatedUser.profileImage, actual: updatedUser.profileImage },
                    { field: 'certificates', value: Array.isArray(updatedLabour.certificates) && updatedLabour.certificates.length > 0, actual: updatedLabour.certificates },
                    { field: 'identification', value: Array.isArray(updatedLabour.identification) && updatedLabour.identification.length > 0, actual: updatedLabour.identification },
                    { field: 'photoID', value: hasPhotoID, actual: updatedLabour.identification },
                    { field: 'policeClearance', value: hasPoliceClearance, actual: updatedLabour.identification },
                    { field: 'experienceSummary', value: !!updatedLabour.experienceSummary, actual: updatedLabour.experienceSummary },
                    { field: 'occupation', value: updatedLabour.occupation, actual: updatedLabour.occupation },
                    { field: 'tickets', value: Array.isArray(updatedLabour.tickets) && updatedLabour.tickets.length > 0, actual: updatedLabour.tickets },
                ];


                const totalFields = completionChecks.length;
                const completedFields = completionChecks.filter(check => check.value).length;
                const profileCompletionPercentage = Math.round((completedFields / totalFields) * 100);
                const isComplete = completionChecks.every(check => check.value);


                labourUpdates.profileCompletionPercentage = profileCompletionPercentage;
                labourUpdates.profileCompletionStatus = isComplete
                    ? statusTicket.profileCompletionStatuses.COMPLETED
                    : statusTicket.profileCompletionStatuses.PENDING;
                labourUpdates.profileCompletedAt = isComplete && !updatedLabour.profileCompletedAt ? new Date() : updatedLabour.profileCompletedAt;


                await Labour.updateOne(
                    { userId },
                    {
                        $set: {
                            profileCompletionStatus: labourUpdates.profileCompletionStatus,
                            profileCompletionPercentage: labourUpdates.profileCompletionPercentage,
                            profileCompletedAt: labourUpdates.profileCompletedAt,
                        }
                    },
                    { session }
                );
            }

            const notificationUserData = {
                _id: updatedUser._id,
                email: updatedUser.email,
                firstName: updatedUser.firstName,
                lastName: updatedUser.lastName,
                phone: updatedUser.phone,
                countryCode: updatedUser.countryCode,
                gender: updatedUser.gender,
                date_of_birth: updatedUser.date_of_birth,
                profileImage: updatedUser.profileImage
                    ? {
                        _id: updatedUser.profileImage._id, filename: updatedUser.profileImage.filename, url: `${aws.S3_BASE_URL}/${updatedUser.profileImage.key}`,
                        fileSize: updatedUser.profileImage.fileSize
                    }
                    : null,
                labourId: updatedLabour._id,
                updatedFields: updatedFields.join(', '),
            };


            if (isSelf && labourRoleId && (currentUserData.roleId as mongoose.Types.ObjectId).equals(labourRoleId as mongoose.Types.ObjectId)) {
                const allAdminsAndStaff = await User.find({
                    roleId: { $in: [adminRoleId, staffRoleId].filter(Boolean) },
                    status: { $in: [StatusEnum.ACTIVE] },
                }).select('_id').session(session).lean();

                const recipients = allAdminsAndStaff.map(u => new Types.ObjectId(u._id));

                if (!isDraft) {
                    notificationQueue.push({
                        module: 'labourManagement',
                        action: 'profileCompleted',
                        data: {
                            from: new Types.ObjectId(userId),
                            to: recipients,
                            entityData: notificationUserData,
                        },
                    });
                }

                if (!updatedLabour.isFirstUpdate && updatedFields.length > 0) {
                    notificationQueue.push({
                        module: 'labourManagement',
                        action: 'labourProfileUpdated',
                        data: {
                            from: new Types.ObjectId(userId),
                            to: recipients,
                            entityData: notificationUserData,
                        },
                    });
                }
            } else if (staffRoleId && (currentUserData.roleId as mongoose.Types.ObjectId).equals(staffRoleId as mongoose.Types.ObjectId)) {
                const allAdmins = await User.find({
                    roleId: adminRoleId,
                    status: { $in: [StatusEnum.ACTIVE] },
                }).select('_id').session(session).lean();

                notificationQueue.push({
                    module: 'labourManagement',
                    action: 'staffUpdatedLabourProfile',
                    data: {
                        from: new Types.ObjectId(currentUser),
                        to: [...allAdmins.map(u => new Types.ObjectId(u._id)), new Types.ObjectId(userId)],
                        entityData: notificationUserData,
                    },
                });
            } else if (adminRoleId && (currentUserData.roleId as mongoose.Types.ObjectId).equals(adminRoleId as mongoose.Types.ObjectId)) {
                const allStaff = await User.find({
                    roleId: staffRoleId,
                    status: { $in: [StatusEnum.ACTIVE] },
                }).select('_id').session(session).lean();

                notificationQueue.push({
                    module: 'labourManagement',
                    action: 'adminUpdatedLabourProfile',
                    data: {
                        from: new Types.ObjectId(currentUser),
                        to: [...allStaff.map(u => new Types.ObjectId(u._id)), new Types.ObjectId(userId)],
                        entityData: notificationUserData,
                    },
                });
            }
            await session.commitTransaction();
            transactionCommitted = true;
            // await Promise.all(
            //     notificationQueue.map(notification =>
            //         this.notification.sendNotification(
            //             notification.module as NotificationModule,
            //             notification.action,
            //             notification.data,
            //             t
            //         )
            //     )
            // );

            const response = {
                _id: updatedLabour._id,
                userId: updatedUser?._id,
                email: updatedUser?.email,
                firstName: updatedUser?.firstName,
                lastName: updatedUser?.lastName,
                status: updatedUser.status,
                phone: updatedUser?.phone,
                gender: updatedUser?.gender,
                countryCode: updatedUser?.countryCode,
                date_of_birth: updatedUser?.date_of_birth,
                notificationPreferences: updatedUser?.notificationPreferences,
                profileImage: updatedUser?.profileImage
                    ? {
                        _id: updatedUser.profileImage._id,
                        filename: updatedUser.profileImage.filename,
                        url: `${aws.S3_BASE_URL}/${updatedUser.profileImage.key}`,
                        fileSize: updatedUser.profileImage.fileSize
                    }
                    : null,
                occupation: updatedLabour?.occupation,
                experienceSummary: updatedLabour?.experienceSummary,
                tickets: Array.isArray(updatedLabour?.tickets)
                    ? updatedLabour.tickets.map(ticketCode => statusTicket.tickets[ticketCode] ?? ticketCode)
                    : [],

                certificates: Array.isArray(updatedLabour?.certificates)
                    ? updatedLabour.certificates.map(cert => ({
                        name: cert.name,
                        file: {
                            _id: cert.file._id,
                            filename: cert.file.filename,
                            url: `${aws.S3_BASE_URL}/${cert.file.key}`,
                            thumbnailUrl: `${aws.S3_BASE_URL}/${cert.file.thumbnailKey}`,
                            fileSize: cert.file.fileSize
                        },
                    }))
                    : [],
                identification: Array.isArray(updatedLabour?.identification)
                    ? updatedLabour.identification.map(id => ({
                        type: id.type,
                        file: {
                            _id: id.file._id,
                            filename: id.file.filename,
                            url: `${aws.S3_BASE_URL}/${id.file.key}`,
                            thumbnailUrl: `${aws.S3_BASE_URL}/${id.file.thumbnailKey}`,
                            fileSize: id.file.fileSize
                        },
                        issuedAt: id.issuedAt,
                        expiresAt: id.expiresAt,
                    }))
                    : [],
                skills: updatedLabour?.skills,
                sitesInductedOn: updatedLabour?.sitesInductedOn,
                draft: updatedLabour?.draft,
                isFirstUpdate: updatedLabour?.isFirstUpdate,
                profileCompletionStatus: updatedLabour?.profileCompletionStatus,
                profileCompletedAt: updatedLabour?.profileCompletedAt,
                profileCompletionPercentage: updatedLabour.profileCompletionPercentage
            };


            return response;
        } catch (error) {
            console.log(error)
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            if (error instanceof HttpException) {
                throw error;
            }
            throw new HttpException(
                status.InternalServerError,
                t('General.errorUpdating', { field: t('Labour.labour') })
            );
        } finally {
            session.endSession();
        }
    }




    public async getAllLabour(params: GetAllLabourParams) {
        const { t, page, limit, availability_status, search, sortBy, sortOrder, skills, occupation, tickets } = params;
        try {
            const filter: any = { isDeleted: false };

            if (availability_status) {
                filter.availability_status = { $regex: new RegExp(availability_status, 'i') };
            }
            if (occupation) {
                const mappedOccupation = mapSingleOccupationCaseInsensitive(occupation);
                if (!mappedOccupation) {
                    throw new HttpException(status.BadRequest, t('General.invalid', { field: 'occupation' }));
                }
                filter.occupation = new RegExp(`^${mappedOccupation}$`, 'i');
            }
            if (tickets && Array.isArray(tickets) && tickets.length > 0) {
                filter.tickets = { $in: tickets.map(t => new RegExp(`^${t}$`, 'i')) };
            }
            const skip = (page - 1) * limit;
            const pipeline: any[] = [
                { $match: filter },
                ...(skills && Array.isArray(skills) && skills.length > 0
                    ? [{
                        $match: {
                            $expr: {
                                $gt: [
                                    {
                                        $size: {
                                            $filter: {
                                                input: '$skills',
                                                as: 'skill',
                                                cond: {
                                                    $in: [
                                                        { $toLower: '$$skill' },
                                                        skills.map(s => s.toLowerCase()),
                                                    ],
                                                },
                                            },
                                        },
                                    },
                                    0,
                                ],
                            },
                        },
                    }]
                    : []),
                {
                    $lookup: {
                        from: 'users',
                        localField: 'userId',
                        foreignField: '_id',
                        as: 'user',
                    },
                },
                {
                    $unwind: {
                        path: '$user',
                        preserveNullAndEmptyArrays: true,
                    },
                },
                {
                    $lookup: {
                        from: 'attachments',
                        localField: 'certificates.file',
                        foreignField: '_id',
                        as: 'certificateFiles',
                    },
                },
                {
                    $lookup: {
                        from: 'attachments',
                        localField: 'identification.file',
                        foreignField: '_id',
                        as: 'identificationFiles',
                    },
                },
                {
                    $lookup: {
                        from: 'attachments',
                        localField: 'user.profileImage',
                        foreignField: '_id',
                        as: 'profileImageData',
                    },
                },
                {
                    $unwind: {
                        path: '$profileImageData',
                        preserveNullAndEmptyArrays: true,
                    },
                },
                {
                    $addFields: {
                        profileImage: {
                            $cond: {
                                if: { $ne: ['$profileImageData', null] },
                                then: {
                                    _id: { $toString: '$profileImageData._id' },
                                    filename: '$profileImageData.filename',
                                    url: {
                                        $concat: [aws.S3_BASE_URL, '/', '$profileImageData.key'],
                                    },
                                    fileSize: '$profileImageData.fileSize'
                                },
                                else: null,
                            },
                        },
                    },
                },
                ...(search
                    ? [{
                        $match: {
                            $or: [
                                { 'user.email': { $regex: new RegExp(search, 'i') } },
                                { 'user.firstName': { $regex: new RegExp(search, 'i') } },
                                { 'user.lastName': { $regex: new RegExp(search, 'i') } },
                                {
                                    $expr: {
                                        $regexMatch: {
                                            input: { $concat: ['$user.firstName', ' ', '$user.lastName'] },
                                            regex: new RegExp(search, 'i'),
                                        },
                                    },
                                },
                            ],
                        },
                    }]
                    : []),
                { $sort: { [sortBy]: sortOrder === 'asc' ? 1 : -1 } },
                { $skip: skip },
                { $limit: limit },
                {
                    $project: {
                        _id: 1,
                        userId: 1,
                        employee_id: 1,
                        sitesInductedOn: 1,
                        occupation: 1,
                        profileCompletionStatus: 1,
                        profileCompletionPercentage: 1,
                        profileCompletedAt: 1,
                        experienceSummary: 1,
                        skills: 1,
                        availability_status: 1,
                        tickets: 1,
                        draft: 1,
                        isFirstUpdate: 1,
                        averageRating: 1,
                        ratingCount: 1,
                        'user.email': 1,
                        'user.firstName': 1,
                        'user.lastName': 1,
                        'user.phone': 1,
                        'user.gender': 1,
                        'user.date_of_birth': 1,
                        'user.notificationPreferences': 1,
                        // 'user.inviteStatus': 1,
                        'user.status': 1,
                        'user.countryCode': 1,
                        profileImage: 1,
                        certificates: {
                            $map: {
                                input: '$certificates',
                                as: 'cert',
                                in: {
                                    name: '$$cert.name',
                                    file: {
                                        $arrayElemAt: [
                                            {
                                                $filter: {
                                                    input: '$certificateFiles',
                                                    as: 'file',
                                                    cond: { $eq: ['$$file._id', '$$cert.file'] },
                                                },
                                            },
                                            0,
                                        ],
                                    },
                                },
                            },
                        },
                        identification: {
                            $map: {
                                input: '$identification',
                                as: 'id',
                                in: {
                                    type: '$$id.type',
                                    file: {
                                        $arrayElemAt: [
                                            {
                                                $filter: {
                                                    input: '$identificationFiles',
                                                    as: 'file',
                                                    cond: { $eq: ['$$file._id', '$$id.file'] },
                                                },
                                            },
                                            0,
                                        ],
                                    },
                                },
                            },
                        },
                    },
                },
            ];
            const countPipeline = [...pipeline.slice(0, -3), { $count: 'totalDocs' }];
            const countResult = await Labour.aggregate(countPipeline);
            const totalDocs = countResult[0]?.totalDocs || 0;


            const labours = await Labour.aggregate(pipeline);
            const data = labours.map(labour => ({
                _id: labour._id,
                userId: labour.userId,
                employee_id: labour.employee_id,
                email: labour.user?.email ?? null,
                firstName: labour.user?.firstName ?? null,
                lastName: labour.user?.lastName ?? null,
                countryCode: labour.user?.countryCode ?? null,
                phone: labour.user?.phone ?? null,
                gender: labour.user?.gender ?? null,
                date_of_birth: labour.user?.date_of_birth ?? null,
                notificationPreferences: labour.user?.notificationPreferences ?? [],
                // inviteStatus: labour.user?.inviteStatus ?? null,
                status: labour.user?.status ?? null,
                skills: labour.skills ?? [],
                ratingCount: labour.ratingCount ?? null,
                averageRating: labour.averageRating ?? null,
                availability_status: labour.availability_status ?? null,
                profileCompletionStatus: labour.profileCompletionStatus ?? null,
                profileCompletionPercentage: labour.profileCompletionPercentage ?? 0,
                profileCompletedAt: labour.profileCompletedAt ?? null,
                experienceSummary: labour.experienceSummary ?? null,
                tickets: Array.isArray(labour.tickets)
                    ? labour.tickets.map(ticketCode => statusTicket.tickets[ticketCode] ?? ticketCode)
                    : [],
                sitesInductedOn: labour.sitesInductedOn ?? [],
                occupation: labour.occupation ?? null,
                draft: labour.draft ?? false,
                isFirstUpdate: labour.isFirstUpdate ?? false,
                profileImage: labour.profileImage
                    ? {
                        _id: labour.profileImage._id,
                        filename: labour.profileImage.filename,
                        url: labour.profileImage.url,
                        fileSize: labour.profileImage.fileSize
                    }
                    : null,
                certificates: (labour.certificates || []).map((cert: any) => ({
                    name: cert.name,
                    file: cert.file
                        ? {
                            _id: cert.file._id,
                            filename: cert.file.filename,
                            url: `${aws.S3_BASE_URL}/${cert.file.key}`,
                            uploadedBy: cert.file.uploadedBy || null,
                            thumbnailUrl: `${aws.S3_BASE_URL}/${cert.file.thumbnailKey}`,
                            fileSize: cert.file.fileSize

                        }
                        : null,
                })),
                identification: (labour.identification || []).map((id: any) => ({
                    type: id.type,
                    file: id.file
                        ? {
                            _id: id.file._id,
                            filename: id.file.filename,
                            url: `${aws.S3_BASE_URL}/${id.file.key}`,
                            uploadedBy: id.file.uploadedBy || null,
                            thumbnailUrl: `${aws.S3_BASE_URL}/${id.file.thumbnailKey}`,
                            fileSize: id.file.fileSize
                        }
                        : null,
                })),
            }));

            return {
                data,
                totalDocs,
                page,
                limit,
                totalPages: Math.ceil(totalDocs / limit),
            };
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(
                status.InternalServerError,
                t('General.errorFetching', { field: t('Labour.labours') })
            );
        }
    }




    public async getById(userId: string, t: TFunction) {
        try {
            const labour = await Labour.findOne({ userId, isDeleted: false })
                .populate<{
                    userId: IUser & { profileImage: IAttachment };
                    certificates: { name: string; file: IAttachment }[];
                    identification: { type: string; file: IAttachment; issuedAt?: Date; expiresAt?: Date }[];
                }>('userId')
                .populate('certificates.file')
                .populate('identification.file')
                .lean();
            if (!labour) {
                throw new HttpException(
                    status.NotFound,
                    t('General.not_found', { field: t('Labour.labour') })
                );
            }

            return {
                _id: labour._id,
                userId: labour.userId._id,
                email: labour.userId?.email || null,
                firstName: labour.userId?.firstName || null,
                lastName: labour.userId?.lastName || null,
                countryCode: labour.userId?.countryCode || null,
                phone: labour.userId?.phone || null,
                gender: labour.userId?.gender || null,
                date_of_birth: labour.userId?.date_of_birth || null,
                notificationPreferences: labour.userId?.notificationPreferences || [],
                profileImage: labour.userId?.profileImage
                    ? {
                        _id: labour.userId.profileImage._id,
                        filename: labour.userId.profileImage.filename,
                        url: `${aws.S3_BASE_URL}/${labour.userId.profileImage.key}`,
                        uploadedBy: labour.userId.profileImage.uploadedBy || null,
                        fileSize: labour.userId.profileImage.fileSize
                    }
                    : null,
                status: labour.userId?.status || null,
                ratingCount: labour.ratingCount ?? null,
                averageRating: labour.averageRating ?? null,
                // inviteStatus: labour.userId?.inviteStatus || null,
                employee_id: labour.employee_id || null,
                skills: labour.skills || [],
                availability_status: labour.availability_status || null,
                profileCompletionStatus: labour.profileCompletionStatus || null,
                profileCompletionPercentage: labour.profileCompletionPercentage || 0,
                profileCompletedAt: labour.profileCompletedAt || null,
                experienceSummary: labour.experienceSummary || null,
                occupation: labour.occupation || null,
                tickets: Array.isArray(labour.tickets)
                    ? labour.tickets.map(ticketCode => statusTicket.tickets[ticketCode] ?? ticketCode)
                    : [],
                sitesInductedOn: labour.sitesInductedOn || [],
                draft: labour.draft || false,
                isFirstUpdate: labour.isFirstUpdate || false,
                certificates: (labour.certificates || []).map(cert => ({
                    name: cert.name,
                    file: cert.file
                        ? {
                            _id: cert.file._id,
                            filename: cert.file.filename,
                            url: `${aws.S3_BASE_URL}/${cert.file.key}`,
                            uploadedBy: cert.file.uploadedBy || null,
                            thumbnailUrl: `${aws.S3_BASE_URL}/${cert.file.thumbnailKey}`,
                            fileSize: cert.file.fileSize
                        }
                        : null,
                })),
                identification: (labour.identification || []).map(id => ({
                    type: id.type,
                    issuedAt: id.issuedAt || null,
                    expiresAt: id.expiresAt || null,
                    file: id.file
                        ? {
                            _id: id.file._id,
                            filename: id.file.filename,
                            url: `${aws.S3_BASE_URL}/${id.file.key}`,
                            uploadedBy: id.file.uploadedBy || null,
                            thumbnailUrl: `${aws.S3_BASE_URL}/${id.file.thumbnailKey}`,
                            fileSize: id.file.fileSize
                        }
                        : null,
                })),
            };
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(
                status.InternalServerError,
                t('General.errorFetching', { field: t('Labour.labour') })
            );
        }
    }





    // public async updateAvailabilityStatus(userId: string, availability_status: string, t: TFunction) {
    //     try {


    //         if (!availability_status || typeof availability_status !== 'string') {
    //             throw new HttpException(
    //                 status.BadRequest,
    //                 t('Labour.invalidAvailabilityStatus', {
    //                     status: availability_status || 'undefined',
    //                 })
    //             );
    //         }

    //         const matchedStatus = Object.values(AvailableStatusEnum).find(
    //             s =>
    //                 typeof s === 'string' &&
    //                 s.toLowerCase() === availability_status.toLowerCase()
    //         );

    //         if (!matchedStatus) {
    //             throw new HttpException(
    //                 status.BadRequest,
    //                 t('Labour.invalidStatus', {
    //                     status: availability_status,
    //                 })
    //             );
    //         }



    //         const labour = await Labour.findOne({ userId, isDeleted: false });
    //         if (!labour) {
    //             throw new HttpException(
    //                 status.NotFound,
    //                 t('General.not_found', { field: t('Labour.labour') })
    //             );
    //         }

    //         if (labour.availability_status === matchedStatus) {
    //             throw new HttpException(
    //                 status.BadRequest,
    //                 t('Labour.alreadyStatus', {
    //                     status: matchedStatus,
    //                 })
    //             );
    //         }

    //         labour.availability_status = matchedStatus;
    //         labour.$session(session)
    //         await labour.save();

    //         return {
    //             _id: labour._id,
    //             availability_status: labour.availability_status,
    //             userId: labour.userId,
    //         };
    //     } catch (error) {

    //         if (error instanceof HttpException) throw error
    //         throw new HttpException(status.InternalServerError, t('General.errorUpdating', { field: t('Labour.labour') }))
    //     }
    // }



}



